/**
 * Reviews — dezelfde vier-koloms-opbouw als Vandaag (ontwerp v4 §7,
 * plak 2.6).
 *
 * WebwinkelKeur en Google in één lijst, de review in het midden, de
 * publieke reactie in de tegel eronder, de copiloot ernaast. De
 * middenkolom en de copiloot zijn van andere bouwers en worden hier
 * gebruikt zoals ze zijn; de copiloot leest zijn zaak uit `?zaak=` in
 * de url, en die zet dit scherm.
 *
 * De tegel onderaan wisselt tussen twee dingen: de publieke reactie
 * (deze plak) en de gedeelde reactietegel van bouwer 2, voor een privé
 * mail aan de klant. Nooit allebei tegelijk, want dan staan er twee
 * verzendknoppen onder elkaar en is het de vraag welke publiek is.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { werkplekReviewsApi } from "@/lib/werkplek-api";

import { WerkplekCopilootKolom } from "../Copiloot/WerkplekCopilootKolom";
import { Reactietegel } from "../Tegel/Reactietegel";
import { Kopcijfers } from "../Vandaag/Kopcijfers";
import { Middenkolom } from "../Vandaag/Middenkolom";
import { maandJaar } from "../Vandaag/tijd";
import "../Vandaag/vandaag.css";
import "../Retouren/retouren.css";
import { PubliekeReactietegel } from "./PubliekeReactietegel";
import { ReviewBlok } from "./ReviewBlok";
import { ReviewsKolom, type ReviewFilterSleutel } from "./ReviewsKolom";
import { useKijktMee } from "../Copiloot/kijktMee";

type Tab = "lijst" | "review" | "copiloot";

function useSmalScherm(): boolean {
  const [smal, setSmal] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 900px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vraag = window.matchMedia("(max-width: 900px)");
    const luister = (e: MediaQueryListEvent) => setSmal(e.matches);
    vraag.addEventListener("change", luister);
    return () => vraag.removeEventListener("change", luister);
  }, []);
  return smal;
}

export function WerkplekReviews() {
  const smal = useSmalScherm();
  const [tab, setTab] = useState<Tab>("lijst");
  const [filter, setFilter] = useState<ReviewFilterSleutel>("reageren");
  const [zoek, setZoek] = useState("");
  const [tegel, setTegel] = useState<"publiek" | "mail">("publiek");
  const automatisch = useRef(false);
  const [params, setParams] = useSearchParams();

  const reviewId = params.get("review");
  const zaakId = params.get("zaak");
  // Sessie 46 (B6): de copiloot kijkt mee op de review en de klant.
  useKijktMee("review", reviewId, "reviews");

  const cijfers = useQuery({
    queryKey: ["werkplek", "reviewcijfers"],
    queryFn: werkplekReviewsApi.cijfers,
  });

  const dossier = useQuery({
    queryKey: ["werkplek", "review", reviewId],
    queryFn: () => werkplekReviewsApi.dossier(reviewId as string),
    enabled: Boolean(reviewId),
  });

  useEffect(() => {
    if (!reviewId) return;
    let afgebroken = false;
    void werkplekReviewsApi
      .zaak(reviewId)
      .then((uitkomst) => {
        if (afgebroken) return;
        setParams(
          (vorige) => {
            const volgende = new URLSearchParams(vorige);
            volgende.set("review", reviewId);
            volgende.set("zaak", uitkomst.zaak_id);
            return volgende;
          },
          { replace: true },
        );
      })
      .catch(() => {
        // Zonder zaak geen middenkolom en geen privé mail; dat zegt het
        // scherm zelf, in plaats van stil leeg te blijven.
      });
    return () => {
      afgebroken = true;
    };
  }, [reviewId, setParams]);

  const kies = (gekozen: string) => {
    // Zelfde grendel als op Retouren: opnieuw op de open review
    // klikken mag de zaak niet uit de URL halen.
    if (gekozen === reviewId) {
      if (smal) setTab("review");
      return;
    }
    setTegel("publiek");
    setParams(
      (vorige) => {
        const volgende = new URLSearchParams(vorige);
        volgende.set("review", gekozen);
        volgende.delete("zaak");
        return volgende;
      },
      { replace: false },
    );
    if (smal) setTab("review");
  };

  const aan = (welke: Tab) => (!smal || tab === welke ? "ja" : "nee");

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>Reviews</h1>
        <span
          className="wp-periode"
          style={{ fontSize: 12, color: "var(--wp-tekst3)" }}
        >
          {maandJaar()}, tot nu toe
        </span>

        <input
          className="wp-zoek"
          type="search"
          value={zoek}
          placeholder="Zoek in reviews"
          aria-label="Zoek in reviews"
          onChange={(e) => setZoek(e.target.value)}
        />

        <Kopcijfers
          cijfers={[
            {
              sleutel: "reageren",
              waarde: String(cijfers.data?.wacht_op_reactie ?? "\u2026"),
              label: "wacht op jouw reactie",
              opKlik: () => setFilter("reageren"),
              tip: "Toont de reviews die op jou wachten",
            },
            ...(cijfers.data?.platforms ?? []).map((platform) => ({
              sleutel: platform.bron,
              // "geen" en niet een teken: een streepje of puntjes leest
              // als "nog aan het laden" terwijl er echt geen cijfer is.
              waarde:
                platform.gemiddelde !== null
                  ? platform.gemiddelde.toLocaleString("nl-NL")
                  : "geen",
              // Kort genoeg om naast de andere cijfers te passen; de
              // tegel liep anders buiten zijn kader (JP, punt 30 van
              // 22-09). Het aantal staat in de tip.
              label: `${platform.label}, ${platform.aantal_maand} deze maand`,
              tip:
                platform.herkomst === "platform"
                  ? `Het cijfer van ${platform.label} zelf`
                  : platform.herkomst === "eigen_telling"
                    ? "Eigen telling over de opgehaalde reviews"
                    : "Geen cijfer bekend",
            })),
          ]}
        />
      </div>

      <div className="wp-inhoud">
        {smal ? (
          <div className="wp-mobtabs" role="tablist" aria-label="Kolommen">
            {(["lijst", "review", "copiloot"] as Tab[]).map((welke) => (
              <button
                key={welke}
                type="button"
                role="tab"
                aria-selected={tab === welke}
                className="wp-knop"
                onClick={() => setTab(welke)}
                style={
                  tab === welke
                    ? {
                        background: "var(--wp-tekst)",
                        color: "var(--wp-paneel)",
                        borderColor: "var(--wp-tekst)",
                      }
                    : undefined
                }
              >
                {welke === "lijst"
                  ? "Lijst"
                  : welke === "review"
                    ? "Review"
                    : "Copiloot"}
              </button>
            ))}
          </div>
        ) : null}

        <div className="wp-kolommen">
          <div className="wp-kolom" data-mob-aan={aan("lijst")}>
            <ReviewsKolom
              filter={filter}
              opFilter={setFilter}
              zoek={zoek}
              gekozenReviewId={reviewId}
              opKiezen={kies}
              opEersteKaart={(id) => {
                if (reviewId || automatisch.current || !id) return;
                automatisch.current = true;
                setParams(
                  (vorige) => {
                    const volgende = new URLSearchParams(vorige);
                    volgende.set("review", id);
                    return volgende;
                  },
                  { replace: true },
                );
              }}
            />
          </div>

          <div className="wp-kolom wp-kolom-midden" data-mob-aan={aan("review")}>
            {!reviewId ? (
              <div className="wp-scrol">
                <div className="wp-leeg" style={{ marginTop: 40 }}>
                  Kies een review links. De review verschijnt hier met de
                  zaak eronder en de reactie in de tegel.
                </div>
              </div>
            ) : dossier.isLoading ? (
              <div className="wp-scrol">
                <div className="wp-leeg">Bezig met laden…</div>
              </div>
            ) : dossier.isError || !dossier.data ? (
              <div className="wp-scrol">
                <div className="wp-leeg" role="alert">
                  Deze review is niet op te halen. Ververs de pagina; blijft
                  dit staan, dan is de API niet bereikbaar.
                </div>
              </div>
            ) : (
              <>
                {/* Eén kolom: de review, daaronder de zaak (JP, punt
                    33 van 22-09). De kop "Google, 5 sterren" stond
                    dubbel — het reviewblok draagt dezelfde bron en
                    hetzelfde cijfer al als chip — en het klantblok van
                    de middenkolom zette er nog een los "Geen klant
                    gevonden" onder. De klantkoppeling staat nu als één
                    rij in de zaak. */}
                <div className="wp-band">
                  <ReviewBlok
                    dossier={dossier.data}
                    zaakId={zaakId}
                    opPriveMail={() => setTegel("mail")}
                  />
                </div>
                <Middenkolom
                  zaakId={zaakId}
                  delen={{
                    kop: false,
                    klantblok: false,
                    zaakblok: false,
                    // De review zelf staat al in ReviewBlok hierboven;
                    // het berichtblok zei hier alleen dat het leeg was
                    // (JP, punt 9 van 22-09).
                    berichtblok: false,
                  }}
                />
                <div className="wp-tegel-vast" style={{ padding: 12 }}>
                  {tegel === "mail" ? (
                    <>
                      <button
                        type="button"
                        className="wp-knop"
                        data-terug-publiek
                        style={{ marginBottom: 8 }}
                        onClick={() => setTegel("publiek")}
                      >
                        Terug naar de publieke reactie
                      </button>
                      {zaakId ? (
                        <Reactietegel zaakId={zaakId} />
                      ) : (
                        <div className="wp-leeg">
                          Er is nog geen zaak bij deze review, dus er kan
                          niets verstuurd worden.
                        </div>
                      )}
                    </>
                  ) : (
                    <PubliekeReactietegel
                      /* Verse tegel per review: geen tekst van de vorige
                         review die per ongeluk publiek gaat. */
                      key={dossier.data.review_id}
                      dossier={dossier.data}
                      zaakId={zaakId}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="wp-kolom" data-mob-aan={aan("copiloot")}>
            <WerkplekCopilootKolom />
          </div>
        </div>
      </div>
    </>
  );
}
